import type { PromptTemplateListParams } from '@saaga/shared/interfaces/prompt-template.interface'
import {
  useGetAgencyPromptTemplatesQuery,
  useGetPromptTemplatesQuery,
} from '@saaga/shared/store/prompt-template-endpoints.store'
import { useMemo } from 'react'

import type { PromptTemplate } from '@/components/chat/prompt-template.interface'
import type {
  DataTableAction,
  DataTablePagination,
  SortOrder,
} from '@/components/data-table/data-table.interface'

import type { PromptTemplateDrawerFormSchemaType } from './prompt-template-drawer-form.schema'
import { createPromptTemplateActionList } from './prompt-template-table-actions.helper'
import { usePromptTemplateCrud } from './use-prompt-template-crud.hook'
import { usePromptTemplateTableState } from './use-prompt-template-table-state.hook'

interface UsePromptTemplateTableProps {
  agencySlug?: string
}

interface UsePromptTemplateTableReturn {
  paginatedData: PromptTemplate[]
  processedTotalCount: number
  searchValue: string
  sortBy: string
  sortOrder: SortOrder
  pagination: DataTablePagination
  isDrawerOpen: boolean
  editingTemplate: PromptTemplate | null
  templateToDelete: PromptTemplate | null
  actionList: DataTableAction<PromptTemplate>[]
  isLoading: boolean
  setSearchValue: (value: string) => void
  handleSortChange: (sortBy: string, sortOrder: SortOrder) => void
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleCreate: () => void
  handleSave: (values: PromptTemplateDrawerFormSchemaType) => void
  setIsDrawerOpen: (isOpen: boolean) => void
  handleDeleteConfirm: () => void
  handleDeleteCancel: () => void
}

const EMPTY_LIST: PromptTemplate[] = []

export const usePromptTemplateTable = ({
  agencySlug,
}: UsePromptTemplateTableProps = {}): UsePromptTemplateTableReturn => {
  const crud = usePromptTemplateCrud({ agencySlug })

  const queryParams: PromptTemplateListParams = useMemo(() => ({ page: 1, pageSize: 100 }), [])

  const tenantQuery = useGetPromptTemplatesQuery(agencySlug ? undefined : queryParams, {
    skip: !!agencySlug,
  })

  const agencyQuery = useGetAgencyPromptTemplatesQuery(
    agencySlug ? { agencySlug, ...queryParams } : { agencySlug: '', ...queryParams },
    { skip: !agencySlug }
  )

  const activeQuery = agencySlug ? agencyQuery : tenantQuery
  const itemList = activeQuery.data?.itemList ?? EMPTY_LIST
  const total = activeQuery.data?.total ?? 0
  const isLoading = activeQuery.isLoading

  const tableState = usePromptTemplateTableState({ itemList, total })

  const actionList = useMemo(
    () => createPromptTemplateActionList(crud.handleEdit, crud.handleDeleteRequest),
    [crud.handleEdit, crud.handleDeleteRequest]
  )

  return {
    ...tableState,
    isDrawerOpen: crud.isDrawerOpen,
    editingTemplate: crud.editingTemplate,
    templateToDelete: crud.templateToDelete,
    actionList,
    isLoading,
    handleCreate: crud.handleCreate,
    handleSave: crud.handleSave,
    setIsDrawerOpen: crud.setIsDrawerOpen,
    handleDeleteConfirm: crud.handleDeleteConfirm,
    handleDeleteCancel: crud.handleDeleteCancel,
  }
}
